import React, { useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import { track } from "statsig";
import { STATSIG } from "statsig/events";
import { buildFeeSheet } from "./feeSheetModel";
import "./closingCostFeeModal.css";

// The pricing engine page's fee sheet ("View fee sheet" in a rate's
// detail beside the rates), laid out like page 2 of the CFPB Loan Estimate:
// Loan Costs on the left, Other Costs and Calculating Cash to Close on the
// right, one column when the dialog is too narrow for two. It is Zeitro's
// estimate, not a Loan Estimate, and says so; no CFPB mark.
//
// The Rates page (MortgageCalculators) and the Non-QM calculator keep their
// own older fee sheet dialogs.

const NARROW = 720; // below this sheet width the two columns stack

export function money(centsValue) {
  const v = Number(centsValue) || 0;
  const text = (Math.abs(v) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${v < 0 ? "−" : ""}$${text}`;
}

const today = () => new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

function Line({ label, amount, className = "" }) {
  return (
    <div className={`ppe-fs-line ${className}`}>
      <span className="ppe-fs-line-label">{label}</span>
      <span className="ppe-fs-amount">{amount === null || amount === undefined ? "" : money(amount)}</span>
    </div>
  );
}

function SectionHead({ title, amount, total }) {
  return (
    <div className={`ppe-fs-bar ${total ? "ppe-fs-bar-total" : ""}`}>
      <span className="ppe-fs-bar-label">{title}</span>
      <span className="ppe-fs-amount">{amount === undefined ? "" : money(amount)}</span>
    </div>
  );
}

function Section({ section, children }) {
  return (
    <section className="ppe-fs-section" aria-label={`${section.letter}. ${section.label}`}>
      <SectionHead title={`${section.letter}. ${section.label}`} amount={section.cents} />
      {children}
      {section.items.map((item, i) => <Line key={`${item.name}-${i}`} label={item.name} amount={item.cents} />)}
    </section>
  );
}

export function FeeSheet({ sheet, sheetRef, narrow }) {
  const { sections, points, cash } = sheet;
  return (
    <div className={`ppe-fs-sheet ${narrow ? "is-narrow" : ""}`} ref={sheetRef} data-fee-sheet="">
      <div className="ppe-fs-top">
        <span className="ppe-fs-note">Zeitro estimate · not a Loan Estimate</span>
        <span className="ppe-fs-meta">
          {sheet.loanAmount > 0 && <span>Loan Amount {money(Math.round(sheet.loanAmount * 100))}</span>}
          {Number.isFinite(sheet.rate) && <span>Interest Rate {sheet.rate.toFixed(3)}%</span>}
          <span>Prepared {today()}</span>
        </span>
      </div>
      <h2 className="ppe-fs-title">Closing Cost Details</h2>
      <div className="ppe-fs-columns">
        <div className="ppe-fs-col">
          <div className="ppe-fs-head">Loan Costs</div>
          <Section section={sections.A}>
            <Line
              className="ppe-fs-points"
              label={`${points ? `${points.percent} ` : ""}% of Loan Amount (Points)`}
              amount={points ? points.cents : null}
            />
          </Section>
          <Section section={sections.B} />
          <Section section={sections.C} />
          <SectionHead title="D. TOTAL LOAN COSTS (A + B + C)" amount={sheet.loanCosts} total />
        </div>
        <div className="ppe-fs-col">
          <div className="ppe-fs-head">Other Costs</div>
          <Section section={sections.E} />
          <Section section={sections.F} />
          <Section section={sections.G} />
          <Section section={sections.H} />
          <SectionHead title="I. TOTAL OTHER COSTS (E + F + G + H)" amount={sheet.otherCosts} total />
          <section className="ppe-fs-section" aria-label="J. Total Closing Costs">
            <SectionHead title="J. TOTAL CLOSING COSTS" amount={sheet.totalClosingCosts} total />
            <Line label="D + I" amount={sheet.loanCosts + sheet.otherCosts} />
            <Line label="Lender Credits" amount={sheet.lenderCredit} />
            {sheet.unexplained !== 0 && <Line label="Other adjustments" amount={sheet.unexplained} />}
          </section>
          <div className="ppe-fs-head ppe-fs-head-cash">Calculating Cash to Close</div>
          <section className="ppe-fs-section ppe-fs-cash" aria-label="Calculating Cash to Close">
            <Line label="Total Closing Costs (J)" amount={cash.totalClosingCosts} />
            <Line label="Closing Costs Financed (Paid from your Loan Amount)" amount={cash.closingCostsFinanced} />
            <Line label="Down Payment/Funds from Borrower" amount={cash.downPayment} />
            <Line label="Deposit" amount={-cash.deposit} />
            <Line label="Funds for Borrower" amount={cash.fundsForBorrower} />
            <Line label="Seller Credits" amount={-cash.sellerCredits} />
            <Line label="Adjustments and Other Credits" amount={cash.adjustmentsAndOtherCredits} />
            <Line label="Estimated Cash to Close" amount={cash.cashToClose} className="ppe-fs-cash-total" />
          </section>
        </div>
      </div>
      <p className="ppe-fs-disclaimer">
        These closing costs are estimates and may change. Actual costs depend on final lender fees, third-party charges and other factors.
      </p>
    </div>
  );
}

// The dialog is two columns wide unless the window is narrower than that.
function useNarrow(node) {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => setNarrow(node.clientWidth > 0 && node.clientWidth < NARROW);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return narrow;
}

// Export: a one-page US Letter PDF of the sheet, always two columns. The
// sheet is drawn with html2canvas (as before), on a copy forced to 960px.
async function exportPdf(node) {
  const [{ default: JsPDF }, { default: html2canvas }] = await Promise.all([import("jspdf"), import("html2canvas")]);
  const canvas = await html2canvas(node, {
    scale: 2,
    backgroundColor: "#ffffff",
    windowWidth: 1200,
    onclone: (doc) => {
      const copy = doc.querySelector("[data-fee-sheet]");
      if (!copy) return;
      copy.classList.remove("is-narrow");
      copy.classList.add("is-export");
      let parent = copy.parentElement;
      while (parent && parent !== doc.body) {
        parent.style.maxHeight = "none";
        parent.style.overflow = "visible";
        parent = parent.parentElement;
      }
    },
  });
  const pdf = new JsPDF({ orientation: "portrait", unit: "mm", format: "letter", compress: true });
  const margin = 12.7;
  const pageWidth = pdf.internal.pageSize.getWidth() - margin * 2;
  const pageHeight = pdf.internal.pageSize.getHeight() - margin * 2;
  const scale = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
  // JPEG: an uncompressed PNG of the page was 11 MB (dev 11-04).
  pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", margin, margin, canvas.width * scale, canvas.height * scale);
  pdf.save("Estimated Fee Sheet.pdf");
}

export default function ClosingCostFeeModal({ show, onHide, filterParams, detailedCard, closingCostsDetail, statsigSource }) {
  const sheetRef = useRef(null);
  const [bodyNode, setBodyNode] = useState(null);
  const [exporting, setExporting] = useState(false);
  const narrow = useNarrow(bodyNode);
  const sheet = buildFeeSheet(detailedCard, closingCostsDetail, filterParams);

  const onExport = async () => {
    if (statsigSource === "rates") track(STATSIG.RATES.VIEW_FEE_SHEET_BUTTON, "export", { type: "export" });
    setExporting(true);
    try {
      await exportPdf(sheetRef.current);
    } catch (err) {
      console.error("Fee sheet export failed", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      open={Boolean(show)}
      onCancel={onHide}
      width={1040}
      // Above the page's other layers (antd drawers are 1000).
      zIndex={1100}
      centered
      className="ppe-fee-sheet-modal"
      closable={{ "aria-label": "Close fee sheet" }}
      destroyOnClose
      footer={(
        <div className="ppe-fs-footer">
          <button type="button" className="ppe-btn ppe-btn-secondary" onClick={onExport} disabled={exporting}>
            {exporting ? "Exporting…" : "Export"}
          </button>
          <button type="button" className="ppe-btn ppe-btn-primary" onClick={onHide}>Close</button>
        </div>
      )}
    >
      <div ref={setBodyNode} className="ppe-fs-body">
        {show && <FeeSheet sheet={sheet} sheetRef={sheetRef} narrow={narrow} />}
      </div>
    </Modal>
  );
}
